import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);

    // Lock body scroll while open. (Drawer is closed inline on link click — no route-change effect needed.)
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    const linkClass = (to, exact = false) => {
        const isActive = exact ? pathname === to : pathname.startsWith(to);
        return isActive ? "text-white" : "text-slate-300 hover:text-white";
    };

    return (
        <header
            data-testid="navbar"
            className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/70 border-b border-white/5"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-2">
                <Link to="/" data-testid="brand-link" className="flex items-center gap-2 shrink-0">
                    <span className="inline-block w-2 h-2 rounded-full bg-gold" />
                    <span className="font-serif text-lg sm:text-xl tracking-tight">
                        The <span className="gold-text italic">Butler</span> Room
                    </span>
                </Link>

                {/* Desktop nav */}
                <nav className="hidden md:flex items-center gap-6 lg:gap-8 text-sm">
                    <Link to="/" className={linkClass("/", true)}>Home</Link>
                    <Link to="/pricing" data-testid="nav-pricing" className={linkClass("/pricing")}>Pricing</Link>
                    <Link to="/partners" data-testid="nav-partners" className={linkClass("/partners")}>Become a Butler Partner</Link>
                    {user && user.role === "client" && <Link to="/dashboard" data-testid="nav-dashboard" className={linkClass("/dashboard")}>My Tasks</Link>}
                    {user && user.role === "partner" && <Link to="/partner" data-testid="nav-partner" className={linkClass("/partner", true)}>My Earnings</Link>}
                    {user?.role === "admin" && <Link to="/admin" data-testid="nav-admin" className={linkClass("/admin", true)}>Butler Desk</Link>}
                    {user?.role === "admin" && <Link to="/admin/partners" data-testid="nav-admin-partners" className={linkClass("/admin/partners")}>Partners Desk</Link>}
                    {user && <Link to="/account" data-testid="nav-account" className={linkClass("/account")}>Account</Link>}
                </nav>

                {/* Desktop CTAs */}
                <div className="hidden sm:flex items-center gap-3 shrink-0">
                    {!user && (
                        <>
                            <Link to="/login" data-testid="nav-login" className="text-sm text-slate-300 hover:text-white">Sign in</Link>
                            <Link to="/register" data-testid="nav-register" className="btn-gold text-sm">Join</Link>
                        </>
                    )}
                    {user && (
                        <>
                            <Link to="/tasks/new" data-testid="nav-new-task" className="btn-gold text-sm">+ New task</Link>
                            <button
                                data-testid="nav-logout"
                                onClick={async () => { await logout(); navigate("/"); }}
                                className="text-sm text-slate-300 hover:text-white"
                            >Sign out</button>
                        </>
                    )}
                </div>

                {/* Mobile hamburger */}
                <button
                    data-testid="nav-mobile-toggle"
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                    onClick={() => setOpen((v) => !v)}
                    className="sm:hidden -mr-2 p-2 text-slate-200 hover:text-gold transition"
                >
                    {open ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
                </button>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    data-testid="nav-mobile-drawer"
                    className="sm:hidden border-t border-white/5 bg-slate-950/95 backdrop-blur-xl"
                >
                    <nav className="px-6 py-6 flex flex-col gap-1 text-base">
                        <MobileLink to="/" testid="nav-mobile-home" close={() => setOpen(false)}>Home</MobileLink>
                        <MobileLink to="/pricing" testid="nav-mobile-pricing" close={() => setOpen(false)}>Pricing</MobileLink>
                        <MobileLink to="/partners" testid="nav-mobile-partners" close={() => setOpen(false)}>Become a Butler Partner</MobileLink>
                        {user?.role === "client" && <MobileLink to="/dashboard" testid="nav-mobile-dashboard" close={() => setOpen(false)}>My Tasks</MobileLink>}
                        {user?.role === "partner" && <MobileLink to="/partner" testid="nav-mobile-partner" close={() => setOpen(false)}>My Earnings</MobileLink>}
                        {user?.role === "admin" && <MobileLink to="/admin" testid="nav-mobile-admin" close={() => setOpen(false)}>Butler Desk</MobileLink>}
                        {user?.role === "admin" && <MobileLink to="/admin/partners" testid="nav-mobile-admin-partners" close={() => setOpen(false)}>Partners Desk</MobileLink>}
                        {user && <MobileLink to="/account" testid="nav-mobile-account" close={() => setOpen(false)}>Account</MobileLink>}

                        <div className="border-t border-white/5 mt-4 pt-4 flex flex-col gap-2">
                            {!user && (
                                <>
                                    <Link to="/login" data-testid="nav-mobile-login" className="btn-ghost text-sm text-center">Sign in</Link>
                                    <Link to="/register" data-testid="nav-mobile-register" className="btn-gold text-sm text-center">Join the Butler Room</Link>
                                </>
                            )}
                            {user && (
                                <>
                                    <Link to="/tasks/new" data-testid="nav-mobile-new-task" className="btn-gold text-sm text-center">+ New task</Link>
                                    <button
                                        data-testid="nav-mobile-logout"
                                        onClick={async () => { setOpen(false); await logout(); navigate("/"); }}
                                        className="btn-ghost text-sm"
                                    >Sign out</button>
                                </>
                            )}
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
}

function MobileLink({ to, testid, close, children }) {
    return (
        <Link
            to={to}
            data-testid={testid}
            onClick={close}
            className="px-2 py-3 text-slate-200 hover:text-gold border-b border-white/5 last:border-b-0 transition"
        >
            {children}
        </Link>
    );
}
